/**
 * Phase 5.5-Lite — Driver at-the-door payment QR.
 *
 * Shown when driver taps "SHOW PAYMENT QR" on an unpaid order.
 * Renders a fullscreen-ish QR with order amount and # so the customer
 * can scan from their phone and pay in Cash App with everything prefilled.
 *
 * Includes a "MARK PAID" button that, combined with the existing combo flow,
 * closes the modal and marks the order paid (and optionally delivered).
 */
import React from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, X } from 'lucide-react';

export function DriverPaymentQRModal({
  open,
  onClose,
  amount,
  orderShortId,
  render,         // { configured, display_name, handle, deep_link, qr_data_url }
  loading,
  onMarkPaidAndDelivered,  // combo button
  onMarkPaidOnly,          // if status not yet 'out_for_delivery'
}) {
  if (!open) return null;
  const configured = render?.configured;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-[#050003] border-[#39FF14] text-[#39FF14] font-mono rounded-none max-w-md"
        data-testid="driver-payment-qr-modal"
      >
        <DialogTitle className="text-[#39FF14] font-mono text-lg uppercase tracking-wider">
          &gt; SHOW THIS TO CUSTOMER_
        </DialogTitle>
        <DialogDescription className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider">
          // Cash App at-door payment
        </DialogDescription>

        {loading && (
          <div className="text-[#A3C7A8] font-mono text-sm py-8 text-center" data-testid="driver-qr-loading">
            // Loading payment QR…
          </div>
        )}

        {!loading && !configured && (
          <div className="text-[#FF3B30] font-mono text-xs p-4 border border-[#FF3B30]" data-testid="driver-qr-unconfigured">
            // CASHAPP_HANDLE_NOT_SET — please collect cash or contact dispatcher.
          </div>
        )}

        {!loading && configured && (
          <div className="space-y-4">
            <div className="bg-[#0C0612] border border-[#2A0F3D] p-4 text-center">
              <p className="text-[#6B8E73] font-mono text-xs uppercase">PAY TO</p>
              <p className="text-[#39FF14] font-mono text-2xl mt-1">{render.display_name}</p>
              <div className="mt-3">
                <p className="text-[#6B8E73] font-mono text-xs uppercase">TOTAL DUE</p>
                <p className="text-[#39FF14] font-mono text-3xl">${Number(amount).toFixed(2)}</p>
                <p className="text-[#6B8E73] font-mono text-xs uppercase mt-2">ORDER #</p>
                <p className="text-[#39FF14] font-mono text-lg">{orderShortId}</p>
              </div>
            </div>

            <div className="flex flex-col items-center gap-2 py-2">
              {render.qr_data_url && (
                <img
                  src={render.qr_data_url}
                  alt="Cash App payment QR code"
                  className="w-64 h-64 border-4 border-[#39FF14] bg-white p-3"
                  data-testid="driver-qr-image"
                />
              )}
              <p className="text-[#A3C7A8] font-mono text-xs uppercase tracking-wider mt-1">
                Have customer scan with their phone
              </p>
              <p className="text-[#4B6B52] font-mono text-[10px] uppercase">Sending to: {render.handle}</p>
            </div>

            <div className="flex flex-col gap-2 pt-2 border-t border-[#2A0F3D]">
              {onMarkPaidAndDelivered && (
                <Button
                  onClick={onMarkPaidAndDelivered}
                  className="w-full bg-[#39FF14] hover:bg-[#2AE54A] text-[#050003] font-mono uppercase tracking-wider rounded-none border-0 h-12"
                  data-testid="driver-mark-paid-delivered"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  &gt; MARK PAID &amp; DELIVERED
                </Button>
              )}
              {onMarkPaidOnly && (
                <Button
                  onClick={onMarkPaidOnly}
                  variant="outline"
                  className="w-full bg-transparent border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-[#050003] font-mono uppercase tracking-wider rounded-none h-12"
                  data-testid="driver-mark-paid-only"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  &gt; MARK PAID (NOT YET DELIVERED)
                </Button>
              )}
              <Button
                onClick={onClose}
                variant="ghost"
                className="text-[#6B8E73] hover:bg-[#2A0F3D] hover:text-[#A3C7A8] font-mono uppercase rounded-none h-9 text-xs"
                data-testid="driver-qr-close"
              >
                <X className="w-3 h-3 mr-1" /> Close
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
